import { useRef } from "react";
import type { ChangeEvent } from "react";
import { useUploadFile } from "../../hooks/useUploadFile";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { Spinner } from "../ui/Spinner";

interface UploadButtonProps {
  folderId: string | null;
}

export function UploadButton({ folderId }: UploadButtonProps) {
  const { config } = useAuth();
  const { push } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadFile(folderId);

  const maxBytes = config?.maxUploadBytes ?? 0;
  const limitMb = Math.floor(maxBytes / 1_048_576);
  const tooLargeMessage = `File exceeds the ${limitMb} MB limit.`;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so re-selecting the same file fires change again.
    e.target.value = "";
    if (!file) {
      return;
    }

    // Client-side pre-check (UX only; server 413 stays authoritative).
    if (maxBytes > 0 && file.size > maxBytes) {
      push({ kind: "error", message: tooLargeMessage });
      return;
    }

    upload.mutate(
      { file },
      {
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.code === "FILE_TOO_LARGE" || err.status === 413) {
              push({ kind: "error", message: tooLargeMessage });
              return;
            }
            if (err.status === 401) {
              // Global handler already redirected; stay silent.
              return;
            }
            push({ kind: "error", message: err.message });
            return;
          }
          push({ kind: "error", message: "Something went wrong. Please try again." });
        },
      },
    );
  };

  const isUploading = upload.isPending;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={onChange}
        aria-hidden="true"
        tabIndex={-1}
      />
      <button
        type="button"
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
        className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-surface-border bg-surface px-4 text-body font-medium text-text transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isUploading ? (
          <>
            <Spinner className="h-4 w-4 text-text-muted" label="Uploading" />
            <span>Uploading {upload.progress}%</span>
          </>
        ) : (
          <>
            <span aria-hidden="true">↑</span>
            <span>Upload</span>
          </>
        )}
      </button>
    </>
  );
}
